import type { SceneBase } from "@scenes/SceneBase";
import * as THREE from "three";
import { Clock } from "three";
import { JourneyCamera } from "./Camera";
import { Postprocessing } from "./Postprocessing";
import { attachResize, createRenderer } from "./Renderer";

export interface WorldConfig {
  canvas: HTMLCanvasElement;
}

/**
 * The singleton orchestrator.
 *
 * Owns:
 *  - the renderer
 *  - the THREE.Scene (master scene — all per-section scenes' groups live inside)
 *  - the JourneyCamera (scroll-driven spline path)
 *  - the Postprocessing composer
 *  - the RAF loop
 *  - the registry of scenes
 *
 * Everything else in the app accesses Three.js only through this object.
 */
export class World {
  readonly canvas: HTMLCanvasElement;
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: JourneyCamera;
  readonly clock = new Clock();
  readonly postfx: Postprocessing;

  private scenes: SceneBase[] = [];
  private rafId = 0;
  private cleanupResize: () => void;
  private globalProgress = 0;
  private scrollVelocity = 0;

  constructor({ canvas }: WorldConfig) {
    this.canvas = canvas;
    this.renderer = createRenderer({ canvas, clearColor: 0x06140f });

    const aspect = window.innerWidth / window.innerHeight;
    this.camera = new JourneyCamera(aspect);

    this.scene.fog = new THREE.FogExp2(0x06140f, 0.018);

    // Hemispheric ambient — set the base lighting key once.
    // Per-scene lights are added by individual scenes.
    const hemi = new THREE.HemisphereLight(0x2f9b73, 0x03100c, 0.4);
    this.scene.add(hemi);

    this.postfx = new Postprocessing({
      renderer: this.renderer,
      scene: this.scene,
      camera: this.camera.camera,
    });

    this.cleanupResize = attachResize(this.renderer, this.camera.camera, (w, h) => {
      this.postfx.setSize(w, h);
      this.camera.resize(w / h);
    });
  }

  /** Register a scene; it becomes part of the journey */
  register(scene: SceneBase): void {
    this.scenes.push(scene);
    this.scene.add(scene.group);
  }

  /** Once all scenes are registered, build the camera spline from their keyframes */
  buildCameraPath(): void {
    const sorted = [...this.scenes].sort((a, b) => a.range[0] - b.range[0]);
    const kfs = sorted.flatMap((s) => s.keyframes);
    if (kfs.length < 2) {
      throw new Error(`[World] Need keyframes from at least 2 scenes; got ${kfs.length}`);
    }
    this.camera.setKeyframes(kfs);
  }

  /** Driven by Lenis via main.ts */
  setScrollProgress(p: number): void {
    this.globalProgress = THREE.MathUtils.clamp(p, 0, 1);
    this.camera.setProgress(this.globalProgress);

    // Activate/deactivate scenes based on whether the camera is in their range
    for (const s of this.scenes) {
      const [a, b] = s.range;
      const inside = this.globalProgress >= a && this.globalProgress <= b;
      if (inside && !s.isActive) s.enter();
      else if (!inside && s.isActive) s.leave();
    }
  }

  setScrollVelocity(v: number): void {
    this.scrollVelocity = v;
    this.postfx.setScrollVelocity(v);
  }

  start(): void {
    const tick = () => {
      this.rafId = requestAnimationFrame(tick);
      const dt = this.clock.getDelta();

      for (const s of this.scenes) {
        const local = (() => {
          const [a, b] = s.range;
          if (this.globalProgress < a || this.globalProgress > b) return undefined;
          return (this.globalProgress - a) / (b - a);
        })();
        s.update(dt, this.globalProgress, local);
      }

      this.postfx.render(dt);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  stop(): void {
    cancelAnimationFrame(this.rafId);
  }

  dispose(): void {
    this.stop();
    this.cleanupResize();
    for (const s of this.scenes) s.dispose();
    this.scenes.length = 0;
    this.postfx.dispose();
    this.renderer.dispose();
  }

  /** Read-only access for debugging (avoid coupling) */
  get progress(): number {
    return this.globalProgress;
  }
  get velocity(): number {
    return this.scrollVelocity;
  }
}
